import { Link } from "react-router-dom";
import {
  FileText,
  BookOpen,
  ShieldCheck,
  HandCoins,
  Users,
  Check,
  ArrowRight,
  ExternalLink,
  Landmark
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";


const services = [
  {
    icon: FileText,
    title: "Form 990 Preparation",
    description: "Accurate, on-time federal information return filing to protect your tax-exempt status.",
    details: [
      "Form 990, 990-EZ & 990-N preparation",
      "Schedule preparation (A, B, O and more)",
      "State charitable filing support",
      "Public disclosure compliance",
      "Deadline tracking and reminders"
    ],
    note: "Filing requirements vary based on your organization's gross receipts and assets."
  },
  {
    icon: ShieldCheck,
    title: "Tax-Exempt Status Support",
    description: "Guidance on obtaining and maintaining 501(c)(3) and other exempt classifications.",
    details: [
      "Form 1023 / 1023-EZ application support",
      "Reasonable compensation review",
      "Unrelated Business Income (UBIT) guidance",
      "Compliance health checks",
      "Annual filing coordination"
    ],
    note: "We provide guidance and preparation; legal determinations rest with the IRS."
  },
  {
    icon: BookOpen,
    title: "Nonprofit Bookkeeping",
    description: "Fund-based accounting that keeps your books board-ready and audit-ready.",
    details: [
      "Fund and grant tracking",
      "Restricted vs. unrestricted funds",
      "Monthly reconciliations",
      "Statement of activities & financial position",
      "Board-ready financial reports"
    ],
    note: "Designed for the unique reporting needs of mission-driven organizations."
  },
  {
    icon: HandCoins,
    title: "Grant & Donation Tracking",
    description: "Keep funders confident with clean, transparent records of every dollar.",
    details: [
      "Donor acknowledgment support",
      "Grant compliance reporting",
      "Contribution recordkeeping",
      "In-kind donation valuation guidance",
      "Year-end giving statements"
    ],
    note: "Helps you meet funder and IRS substantiation requirements."
  }
];

const benefits = [
  {
    icon: ShieldCheck,
    title: "Protect Your Status",
    description: "Stay compliant and keep your hard-earned tax-exempt status secure."
  },
  {
    icon: Users,
    title: "Build Donor Trust",
    description: "Transparent records give funders and boards confidence in your mission."
  },
  {
    icon: Landmark,
    title: "Focus on Your Mission",
    description: "Let us handle the numbers so you can focus on the work that matters."
  }
];

export default function NonProfit() {
  return (
    <div data-testid="nonprofit-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Non-Profit Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Accounting Built for Mission-Driven Organizations
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300 mb-8">
              From Form 990 preparation to fund-based bookkeeping, we help non-profits stay compliant, build donor trust, and focus on their mission.
            </p>
            <Button
              asChild
              className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
              data-testid="nonprofit-cta-btn"
            >
              <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                Book Consultation
                <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="flex items-center gap-4" data-testid={`nonprofit-benefit-${index}`}>
                <div className="w-14 h-14 rounded-full bg-bone flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-7 h-7 text-bronze" />
                </div>
                <div>
                  <h3 className="font-body font-semibold text-navy">{benefit.title}</h3>
                  <p className="font-body text-gray-600 text-sm">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-mono text-bronze text-sm mb-3 tracking-wider uppercase">Our Services</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-4">
              Non-Profit Support Services
            </h2>
            <p className="font-body text-gray-600 max-w-2xl mx-auto">
              Specialized services to help your organization stay compliant and financially healthy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card
                key={service.title}
                className="bg-white border-0 shadow-lg"
                data-testid={`nonprofit-service-${index}`}
              >
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center flex-shrink-0">
                      <service.icon className="w-7 h-7 text-bronze" />
                    </div>
                    <div>
                      <CardTitle className="font-heading text-xl text-navy mb-2">
                        {service.title}
                      </CardTitle>
                      <p className="font-body text-gray-600">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {service.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 font-body text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                  {service.note && (
                    <p className="font-body text-xs text-gray-500 italic pt-4 border-t border-gray-100">
                      {service.note}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Block */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">Why Choose Us</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy mb-6">
                We Understand Non-Profit Finances
              </h2>
              <p className="font-body text-gray-600 mb-8">
                Non-profit accounting isn't the same as for-profit bookkeeping. Fund tracking, donor restrictions, and IRS reporting all require specialized care. We speak the language of mission-driven organizations.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Understand Your Mission</h4>
                    <p className="font-body text-gray-600 text-sm">We learn your programs, funding, and reporting obligations.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Set Up Clean Systems</h4>
                    <p className="font-body text-gray-600 text-sm">Fund accounting and reporting tailored to non-profits.</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-bronze/20 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="font-mono text-bronze font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-body font-semibold text-navy">Stay Compliant Year-Round</h4>
                    <p className="font-body text-gray-600 text-sm">Ongoing support so filings and books are always ready.</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-bone rounded-2xl p-8 lg:p-12">
              <h3 className="font-heading text-2xl font-bold text-navy mb-6">
                Ready to Strengthen Your Organization?
              </h3>
              <p className="font-body text-gray-600 mb-8">
                Whether you're just forming or need help with your annual filings, we're here to support your mission. Book a consultation to get started.
              </p>
              <div className="space-y-4">
                <Button
                  asChild
                  className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 font-medium"
                  data-testid="nonprofit-book-btn"
                >
                  <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                    Book Free Consultation
                    <ExternalLink className="ml-2 w-4 h-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="w-full border-navy text-navy hover:bg-navy hover:text-white rounded-full py-6 font-medium"
                  data-testid="nonprofit-contact-btn"
                >
                  <Link to="/contact">
                    Contact Us
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Questions About Non-Profit Services?
          </h2>
          <p className="font-body text-gray-300 mb-8 max-w-2xl mx-auto">
            Every organization is unique. Let's talk about how we can support your mission.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="nonprofit-cta-final"
          >
            <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
              Schedule a Call
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
